import {
  OrganisationPermission,
  OrganisationUserRead,
  useListOrganisationUsersQuery,
  useUpdateOrganisationUserPermissionsMutation,
  useRemoveOrganisationUserMutation,
} from "store/api/generatedApi";
import { IconButton, List, ListItem, ListItemText, Stack } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { isFetchBaseQueryError } from "helpers";
import { MultiSelect } from "src/components/common/inputs/multiSelect";
import { Fetching } from "src/components/common/loading/fetching";
import { ORGANISATION_PERMISSION_VALUES } from "components/organisationUsers/organisationPermissions.ts";
import { useTranslation } from "react-i18next";
import { useConfirmDialog } from "src/contexts/confirmDialog";

export const OrganisationUserList = ({
  groupName,
  onError,
}: {
  groupName: string;
  onError: (message: string) => void;
}) => {
  const { data, isFetching } = useListOrganisationUsersQuery({ groupName });
  const activeUsers = data?.users.filter((user) => user.disabled_at === null);

  return (
    <Fetching fetching={isFetching}>
      <List>
        {activeUsers?.map((user) => (
          <OrganisationUserListItem
            key={`${groupName}/${user.uid}`}
            groupName={groupName}
            user={user}
            onError={onError}
          />
        ))}
      </List>
    </Fetching>
  );
};

const OrganisationUserListItem = ({
  groupName,
  user,
  onError,
}: {
  groupName: string;
  user: OrganisationUserRead;
  onError: (message: string) => void;
}) => {
  const { t } = useTranslation();
  const confirm = useConfirmDialog();
  const [updatePermissions] = useUpdateOrganisationUserPermissionsMutation();
  const [removeUser] = useRemoveOrganisationUserMutation();

  const setPermissions = (permissions: OrganisationPermission[]) => {
    updatePermissions({
      groupName,
      uid: user.uid,
      updateOrganisationUserRequest: { permissions },
    })
      .unwrap()
      .catch((err: unknown) => {
        if (isFetchBaseQueryError(err)) {
          onError(`Couldn't set user permissions (status code: ${err.status})`);
        }
      });
  };

  const handleRemove = async () => {
    const confirmed = await confirm({
      title: t("removeOrganisationUser"),
      message: t("confirmRemoveOrganisationUser", { email: user.email }),
    });
    if (!confirmed) return;

    removeUser({ groupName, uid: user.uid })
      .unwrap()
      .catch((err: unknown) => {
        if (isFetchBaseQueryError(err)) {
          onError(
            `Couldn't remove user ${user.email} (status code: ${err.status})`
          );
        }
      });
  };

  return (
    <ListItem key={user.uid}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ width: "100%" }}
        spacing={1}
      >
        <ListItemText primary={user.name} secondary={user.email} />
        <Stack direction="row" spacing={1} alignItems="center">
          <MultiSelect<OrganisationPermission, OrganisationPermission>
            options={Array.from(ORGANISATION_PERMISSION_VALUES)}
            getOptionId={(permission) => permission}
            getOptionLabel={(permission) => permission}
            values={user.permissions}
            label="Permissions"
            setValues={(permissions) => {
              setPermissions(permissions);
            }}
            sx={{ minWidth: 200 }}
          />
          <IconButton onClick={() => void handleRemove()}>
            <DeleteIcon />
          </IconButton>
        </Stack>
      </Stack>
    </ListItem>
  );
};
